// Shapes tool-call payloads for reading in the details panel.

import type { TraceStep } from './types';

/** Badge to show for a tool call, based on what the step carries. */
export function toolCallStatus(step: TraceStep): 'worked' | 'failed' | 'waiting' | 'hidden' {
  if (!step.done || step.outcome === 'awaiting_approval') return 'waiting';
  if (step.outcome === 'error') return 'failed';
  return step.toolOutput === undefined ? 'hidden' : 'worked';
}

/** Turns a tool payload key into a readable label. */
export function toolFieldLabel(key: string): string {
  return key
    .replaceAll(/([a-z\d])([A-Z])/g, '$1 $2')
    .replaceAll(/[_-]+/g, ' ')
    .trim()
    .replaceAll(/\b\w/g, (letter) => letter.toUpperCase())
    .replaceAll(/\bId\b/g, 'ID');
}

/** ISO 8601 date, with or without a time part. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * Formats API timestamps for reading. Kept in UTC so the value still matches what the
 * provider reported — shifting it to the viewer's zone can move an order to another day.
 */
export function formatTimestamp(value: string, month: 'short' | 'long' = 'short'): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const hasTime = value.includes(':');
  return parsed.toLocaleString('en-US', {
    month,
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
    ...(hasTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {}),
  });
}

/** Renders a tool payload value, keeping zero and false readable. */
export function toolFieldValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string' && ISO_DATE.test(value)) return formatTimestamp(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Transport details a reviewer never needs — cursors, paging flags, truncation markers. */
const TOOL_FIELD_NOISE = new Set([
  'pageinfo',
  'cursor',
  'nextcursor',
  'previouscursor',
  'hasnextpage',
  'haspreviouspage',
  'typename',
]);

/** Envelope keys that only wrap a payload; their children read better without them. */
const TOOL_FIELD_WRAPPERS = new Set(['input', 'args', 'arguments', 'payload', 'data', 'body', 'result', 'output', 'response']);
const MAX_ARRAY_ITEMS = 10;

function normaliseKey(key: string): string {
  return key.toLowerCase().replaceAll(/[^a-z]/g, '');
}

function isNoiseKey(key: string): boolean {
  return TOOL_FIELD_NOISE.has(normaliseKey(key));
}

/** A payload row, or a titled group of rows for a nested object or list. */
export type ToolField =
  | { kind: 'row'; label: string; value: string }
  | { kind: 'group'; label: string; children: ToolField[] };

/** Size-cap markers Brahma and the backend add in place of what they cut, keyed exactly. */
const TRUNCATION_KEYS = new Map([['_truncated_fields', 'fields'], ['_truncated_items', 'items'], ['_truncated', '']]);

/** `[{ key, value }]` lists (custom attributes, note attributes) read best as one row per pair. */
function isKeyValueList(items: unknown[]): items is { key: unknown; value: unknown }[] {
  return items.every((item) => {
    const record = item && typeof item === 'object' && !Array.isArray(item) ? Object.keys(item) : [];
    return record.length === 2 && record.includes('key') && record.includes('value');
  });
}

/** Nests a tool payload by its own structure; every row is labelled with its own key only. */
export function toolFieldTree(payload: unknown): ToolField[] {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return [{ kind: 'row', label: 'Result', value: toolFieldValue(payload) }];
  }

  const fields = Object.entries(payload as Record<string, unknown>).flatMap(([key, value]): ToolField[] => {
    if (isNoiseKey(key)) return [];
    const unit = TRUNCATION_KEYS.get(key);
    if (unit !== undefined) {
      const cut = typeof value === 'number' ? `${value} ${unit}`.trim() : 'Some data';
      return [{ kind: 'row', label: 'Not shown (size limit)', value: cut }];
    }
    const label = toolFieldLabel(key);

    if (Array.isArray(value)) {
      if (value.length === 0) return [{ kind: 'row', label, value: 'None' }];
      if (value.every((item) => item === null || typeof item !== 'object')) {
        return [{ kind: 'row', label, value: value.map((item) => toolFieldValue(item)).join(', ') }];
      }
      // A single entry needs no numbered sub-group; its fields sit directly under the list.
      if (value.length === 1 && !isKeyValueList(value)) {
        const children = toolFieldTree(value[0]);
        return [children.length > 0 ? { kind: 'group', label, children } : { kind: 'row', label, value: 'None' }];
      }
      const shown = value.slice(0, MAX_ARRAY_ITEMS);
      const children: ToolField[] = isKeyValueList(shown)
        ? shown.map((item) => ({ kind: 'row', label: toolFieldValue(item.key), value: toolFieldValue(item.value) }))
        : shown.map((item, index) => ({ kind: 'group', label: `${label} ${index + 1}`, children: toolFieldTree(item) }));
      if (value.length > MAX_ARRAY_ITEMS) {
        children.push({ kind: 'row', label: 'Not shown', value: `${value.length - MAX_ARRAY_ITEMS} items` });
      }
      return [{ kind: 'group', label: `${label} (${value.length})`, children }];
    }
    if (value && typeof value === 'object') {
      const children = toolFieldTree(value);
      if (children.length === 0) return [{ kind: 'row', label, value: 'None' }];
      // Wrapper keys only envelope the payload, so their children sit at this level.
      return TOOL_FIELD_WRAPPERS.has(normaliseKey(key)) ? children : [{ kind: 'group', label, children }];
    }
    return [{ kind: 'row', label, value: toolFieldValue(value) }];
  });

  // Plain values first: JSONB storage sorts keys by length, which buries status under line items.
  return [...fields.filter((field) => field.kind === 'row'), ...fields.filter((field) => field.kind === 'group')];
}
